import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { Consolidation } from '@returnmates/client-core/src/graphql/generated/api'
import {
  getContainersSelector,
  getCurrentSidebarConsolidationSelector,
  getPartners as getPartnersSelector,
} from '@returnmates/client-core/src/selectors/admin'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import { checkIsAddressChanged } from '@returnmates/client-core/src/utils/checkIsAddressChanged'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import Button from '@returnmates/ui-core/src/components/Button'
import ErrorBlock from '@returnmates/ui-core/src/components/ErrorBlock'
import clsx from 'clsx'
import { FORM_ERROR } from 'final-form'
import arrayMutators from 'final-form-arrays'
import moment from 'moment'
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'

import { FLYOUT_SIDEBAR_TYPES } from '../../constants/flyoutSidebar'
import { FlyoutSidebarContext } from '../../pages/Main'
import EditConsolidationFormInner from './components/EditConsolidationFormInner'
import useStyles from './styles'

interface Props {
  id: string
  type: string
}

function EditConsolidation({ id, type }: Props) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { handleCloseSidebar } = useContext(FlyoutSidebarContext)
  const partners = useSelector(getPartnersSelector)
  const currentConsolidation = useSelector(getCurrentSidebarConsolidationSelector)
  const containers = useSelector(getContainersSelector)
  const [isUpdateConsolidationLoading, setIsUpdateConsolidationLoading] = useState(false)

  const currentContainer = useMemo(() => containers?.find(container => container.id === id), [
    containers,
    id,
  ])

  const currentPartner = useMemo(
    () =>
      partners.find(
        p => p.code === currentConsolidation?.partner.code || currentContainer?.partner.code,
      ),

    [partners, currentConsolidation?.partner.code, currentContainer?.partner.code],
  )

  const updateAddress = useCallback(
    async address => {
      const partnerDestination = currentPartner?.destinations?.find(
        item => item?.address.id === address?.id,
      )

      if (
        checkIsAddressChanged({
          updatedAddress: address,
          currentAddress: partnerDestination?.address,
        }) &&
        type !== FLYOUT_SIDEBAR_TYPES.EDIT_CONTAINER
      ) {
        try {
          await createAsyncAction(
            dispatch,
            actions.updateAddress.request({
              id: address.id,
              input: {
                name: address.name,
                phone: address.phone,
                street: address.street,
                city: address.city,
                state: address.state,
                zipCode: address.zipCode,
                unit: address.unit,
                instructions: address.instructions,
                isValid: address.isValid,
              },
            }),
          )
        } catch (err) {
          const { message } = err as Error

          dispatch(
            actions.addSnackBar.request({
              type: SnackBarStatuses.ERROR,
              message: errorMapper(message || (err as string)),
            }),
          )
        }
      }
    },
    [currentPartner?.destinations, dispatch, type],
  )

  const updateConsolidation = useCallback(
    async (consolidation: Consolidation) => {
      const partnerDestination = currentPartner?.destinations?.find(
        item => item?.address.id === consolidation.address?.id,
      )
      const { id, status, trackingId, carrier, expectedDeliveryDate, weight } = consolidation

      setIsUpdateConsolidationLoading(true)

      try {
        await updateAddress(consolidation.address)

        await createAsyncAction(
          dispatch,
          actions.updateConsolidation.request({
            id,
            input: {
              status,
              trackingId,
              carrierId: carrier?.id,
              expectedDeliveryDate: moment(expectedDeliveryDate).isValid()
                ? moment(expectedDeliveryDate).format('YYYY-MM-DD')
                : null,
              weight,
              partnerDestinationId: partnerDestination?.id,
              labelUrl: consolidation.labelUrl,
            },
          }),
        )
      } catch (err) {
        const { message } = err as Error

        return { [FORM_ERROR]: errorMapper(message || (err as string)) }
      } finally {
        setIsUpdateConsolidationLoading(false)
      }
    },
    [currentPartner?.destinations, dispatch, updateAddress],
  )

  const formatConsolidation = useCallback((consolidation?: Consolidation) => {
    return {
      ...consolidation,
      createdAt: consolidation?.createdAt
        ? moment.unix(consolidation?.createdAt).format('MM-DD-yyyy')
        : null,
      expectedDeliveryDate: consolidation?.expectedDeliveryDate
        ? moment(consolidation?.expectedDeliveryDate).format('MM-DD-yyyy')
        : null,
    }
  }, [])

  useEffect(() => {
    return () => {
      dispatch(actions.clearCurrentSidebarConsolidation.request())
    }
  }, [dispatch])

  return (
    <Form
      initialValues={formatConsolidation(currentConsolidation || currentContainer) || {}}
      onSubmit={updateConsolidation}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, submitError }) => (
        <>
          <EditConsolidationFormInner
            id={id}
            type={type}
            partnerCode={currentConsolidation?.partner?.code || currentContainer?.partner?.code}
          />
          <ErrorBlock message={submitError} className={classes.errorBlock} />
          <div className={classes.buttonsContainer}>
            <Button
              onClick={handleCloseSidebar}
              label="Cancel"
              className={clsx(classes.button, classes.cancelButton)}
              cancelButton
            />
            <Button
              onClick={handleSubmit}
              label="Save"
              isLoading={isUpdateConsolidationLoading}
              className={clsx(classes.button, classes.saveButton)}
            />
          </div>
        </>
      )}
    />
  )
}

export default memo(EditConsolidation)
